import React, { useMemo } from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const FIELD_TYPE_LABELS = {
  text: 'Text',
  textarea: 'Paragraph',
  checkbox: 'Acknowledgement',
  signature: 'Signature',
  date: 'Date',
  number: 'Number',
  email: 'Email',
  select: 'Dropdown',
  radio: 'Choice',
  title: 'Heading'
};

const TemplatePreview = ({ open, onClose, template }) => {
  const schema = template?.schema || {};
  const fields = schema.fields || [];
  const attachments = schema.attachments || [];

  const orderedFields = useMemo(
    () => [...fields].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
    [fields]
  );

  const renderField = (field, index) => {
    const label = field.label || field.field_key;
    const required = Boolean(field.required);
    const key = field.id || `${field.field_key}-${index}`;
    const helper = FIELD_TYPE_LABELS[field.field_type] || 'Field';

    switch (field.field_type) {
      case 'title':
        return (
          <Box key={key} sx={{ pb: 3 }}>
            <Typography variant="overline" color="primary" sx={{ letterSpacing: 3 }}>
              Section {index + 1}
            </Typography>
            <Typography
              variant="h4"
              sx={{ fontWeight: 600, color: 'rgba(41, 55, 85, 0.95)', fontFamily: '"Source Serif Pro", serif' }}
            >
              {field?.config?.headingText || label}
            </Typography>
            {field?.config?.subtitle && (
              <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
                {field.config.subtitle}
              </Typography>
            )}
          </Box>
        );

      case 'checkbox':
        return (
          <Box key={key} sx={{ py: 2.5 }}>
            <Typography variant="body1" sx={{ display: 'flex', gap: 1 }}>
              <span style={{ fontFamily: 'serif', fontSize: 19, color: 'rgba(41,55,85,0.7)' }}>☐</span>
              <span>
                {label}
                {required ? ' *' : ''}
              </span>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {helper}
            </Typography>
          </Box>
        );

      case 'signature':
        return (
          <Box key={key} sx={{ py: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {label}
              {required ? ' *' : ''}
            </Typography>
            <Box
              sx={{
                mt: 3,
                borderBottom: '2px solid rgba(41,55,85,0.55)',
                height: 52,
                display: 'flex',
                alignItems: 'flex-end',
                fontFamily: '"Dancing Script", "Pacifico", cursive',
                fontSize: 26,
                color: 'rgba(41,55,85,0.7)'
              }}
            >
              Signature will appear here
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              Signed electronically via Dailey Forms
            </Typography>
          </Box>
        );

      default: {
        const isLong = field.field_type === 'textarea';
        return (
          <Box key={key} sx={{ py: 2.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {label}
              {required ? ' *' : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {helper}
            </Typography>
            <Box
              sx={{
                mt: 1.5,
                minHeight: isLong ? 110 : 50,
                borderBottom: '1px solid rgba(41,55,85,0.35)',
                fontFamily: '"Source Serif Pro", serif',
                fontSize: isLong ? 16 : 15,
                color: 'rgba(41,55,85,0.55)',
                lineHeight: isLong ? 1.7 : 1.3,
                pb: isLong ? 1 : 0
              }}
            >
              {isLong
                ? 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Vivamus sed…'
                : field.placeholder || 'Sample response'}
            </Box>
            {field?.config?.helpText && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {field.config.helpText}
              </Typography>
            )}
          </Box>
        );
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pr: 6 }}>
        Preview · {template?.name}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 16, top: 16 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: 'rgba(41,55,85,0.06)', py: 5 }}>
        <Box
          sx={{
            mx: 'auto',
            maxWidth: 780,
            bgcolor: '#fff',
            borderRadius: 3,
            boxShadow: '0 28px 60px rgba(41,55,85,0.18)',
            border: '1px solid rgba(41,55,85,0.12)',
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              px: 5,
              py: 4.5,
              borderBottom: '1px solid rgba(41,55,85,0.16)',
              background: 'linear-gradient(125deg, rgba(41,55,85,0.12), rgba(255,255,255,0.95))'
            }}
          >
            <Stack spacing={1.5}>
              <Typography variant="overline" color="primary" sx={{ letterSpacing: 4 }}>
                Dailey Forms Document Preview
              </Typography>
              <Typography
                variant="h3"
                sx={{ fontWeight: 600, color: 'rgba(41,55,85,0.95)', fontFamily: '"Source Serif Pro", serif' }}
              >
                {template?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                The layout below mirrors the final document your recipients will review and sign.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {schema.template_type === 'pdf' ? 'PDF template' : 'Builder template'} · Version
                {' '}
                {template?.version || 1} ·{' '}
                {template?.status === 'published' ? 'Ready to send' : 'Draft'}
              </Typography>
            </Stack>
          </Box>

          <Box sx={{ px: 5, py: 4.5 }}>
            {schema.description && (
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 4, fontFamily: '"Source Serif Pro", serif' }}
              >
                {schema.description}
              </Typography>
            )}

            {schema.template_type === 'pdf' ? (
              <Box
                sx={{
                  borderRadius: 2,
                  border: '1px dashed rgba(41,55,85,0.25)',
                  backgroundColor: 'rgba(41,55,85,0.03)',
                  textAlign: 'center',
                  py: 4,
                  px: 3
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  PDF preview coming soon. Manage overlays inside the template designer.
                </Typography>
              </Box>
            ) : (
              <Stack spacing={3}>{orderedFields.map(renderField)}</Stack>
            )}

            {attachments.length > 0 && (
              <Box sx={{ mt: 5 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle1" gutterBottom>
                  Attachments ({attachments.length})
                </Typography>
                <Stack spacing={1.5}>
                  {attachments.map((attachment) => (
                    <Box
                      key={attachment.id}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid rgba(41,55,85,0.14)',
                        background: 'linear-gradient(145deg, #ffffff, #f7f8fa)'
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {attachment.file_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Stored at: {attachment.storage_path}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default TemplatePreview;
